import apiClient from './client';
import type { PlanType } from '@/models/Plan';

export interface EventPlanResponse {
  plan: PlanType;
  planDetails?: {
    name: string;
    price: number;
    currency: string;
    features: string[];
    limits: Record<string, number>;
  };
}

export interface UpgradeEventPlanRequest {
  plan: PlanType;
  useCredit?: boolean;
  creditId?: number;
}

export interface UpgradeEventPlanResponse {
  amount?: number;
  currency?: string;
  upgradeFrom?: PlanType;
  upgradeTo: PlanType;
  eventUuid: string;
  requiresPayment: boolean;
  proratedAmount?: number;
  creditRecovered?: boolean;
}

export interface UserCreditsResponse {
  standard: number;
  premium: number;
  credits: {
    standard: Array<{
      id: number;
      type: 'standard';
      createdAt: string;
    }>;
    premium: Array<{
      id: number;
      type: 'premium';
      createdAt: string;
    }>;
  };
}

/**
 * Get event's current plan.
 */
export async function getEventPlan(eventUuid: string): Promise<EventPlanResponse> {
  const response = await apiClient.get<{ message_key?: string; data?: EventPlanResponse } | EventPlanResponse>(
    `/events/${eventUuid}/plan`
  );
  
  let data = response.data;
  
  // Handle wrapped response format { message_key, data }
  if (data && typeof data === 'object' && 'data' in data && data.data) {
    data = data.data;
  }
  
  return data as EventPlanResponse;
}

/**
 * Upgrade event plan (returns payment info or confirms upgrade if using credit).
 */
export async function upgradeEventPlan(
  eventUuid: string,
  plan: PlanType,
  useCredit?: boolean,
  creditId?: number
): Promise<UpgradeEventPlanResponse> {
  const response = await apiClient.post<{ message_key?: string; data?: UpgradeEventPlanResponse } | UpgradeEventPlanResponse>(
    `/events/${eventUuid}/upgrade`,
    {
      plan,
      useCredit: useCredit ?? false,
      creditId,
    }
  );
  
  let data = response.data;
  
  // Handle wrapped response format { message_key, data }
  if (data && typeof data === 'object' && 'data' in data && data.data) {
    data = data.data;
  }
  
  return data as UpgradeEventPlanResponse;
}

/**
 * Get user's available credits.
 */
export async function getUserCredits(): Promise<UserCreditsResponse> {
  const response = await apiClient.get<{ message_key?: string; data?: UserCreditsResponse } | UserCreditsResponse>(
    '/users/credits'
  );
  
  let data = response.data;
  
  // Handle wrapped response format { message_key, data }
  if (data && typeof data === 'object' && 'data' in data && data.data) {
    data = data.data;
  }
  
  return data as UserCreditsResponse;
}

